import { create } from 'zustand';
import api from '@/lib/axios';
import toast from 'react-hot-toast';
import type { Product } from '@/features/products/api/product.api';

export interface CartItem extends Product {
    quantity: number;
    cartItemId?: string; // ID of the cart item (not product ID) - used for API calls
    variant?: string;
}

interface CartState {
    items: CartItem[];
    // Computed values - calculated dynamically from items
    totalItems: number;
    totalPrice: number;
    isLoading: boolean;
    // Actions
    fetchCart: () => Promise<void>;
    addToCart: (product: Product, quantity?: number, variant?: string) => Promise<void>;
    removeFromCart: (productId: string) => Promise<void>;
    increaseQuantity: (productId: string) => Promise<void>;
    decreaseQuantity: (productId: string) => Promise<void>;
    updateQuantity: (productId: string, quantity: number) => Promise<void>;
    clearCart: () => Promise<void>;
}

// Helper function to calculate totals dynamically
const calculateTotals = (items: CartItem[]) => {
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    return { totalItems, totalPrice };
};

export const useCartStore = create<CartState>()((set, get) => {
    // Helper to update state with recalculated totals
    const updateState = (items: CartItem[]) => {
        const { totalItems, totalPrice } = calculateTotals(items);
        set({ items, totalItems, totalPrice });
    };

    return {
        items: [],
        totalItems: 0,
        totalPrice: 0,
        isLoading: false,

        fetchCart: async () => {
            set({ isLoading: true });
            try {
                const response = await api.get('/cart');
                const rawItems = response.data.data?.items || [];

                // Transform API response to CartItem format
                // API returns: items[].product (populated), items[].quantity, items[].price, items[]._id (cart item ID)
                // We need: CartItem with flattened product data + cartItemId
                interface RawCartItem {
                    _id: string;
                    product: Product;
                    quantity: number;
                    price: number;
                    variant?: string;
                }

                const items: CartItem[] = (rawItems as RawCartItem[]).map((rawItem) => {
                    const product = rawItem.product || rawItem;
                    return {
                        _id: product._id || rawItem._id, // Product ID
                        name: product.name,
                        slug: product.slug,
                        description: product.description || '',
                        shortDescription: product.shortDescription,
                        price: rawItem.price || product.price,
                        comparePrice: product.comparePrice,
                        sku: product.sku || '',
                        stock: product.stock || 0,
                        sold: product.sold || 0,
                        images: product.images || [],
                        category: product.category || { _id: '', name: '', slug: '' },
                        brand: product.brand,
                        tags: product.tags || [],
                        ratings: product.ratings || { average: 0, count: 0 },
                        isFeatured: product.isFeatured || false,
                        isActive: product.isActive !== false,
                        createdAt: product.createdAt || new Date().toISOString(),
                        updatedAt: product.updatedAt || new Date().toISOString(),
                        quantity: rawItem.quantity,
                        cartItemId: rawItem._id?.toString(), // Cart item ID (not product ID) - used for API calls
                        variant: rawItem.variant,
                    };
                });

                updateState(items);
            } catch (error) {
                console.error('Failed to fetch cart:', error);
                // If cart doesn't exist on server, keep local state
            } finally {
                set({ isLoading: false });
            }
        },

        addToCart: async (product: Product, quantity: number = 1, variant?: string) => {
            set({ isLoading: true });
            try {
                // Optimistic update - add to local state first
                const { items } = get();
                const existingItemIndex = items.findIndex(
                    (item) => item._id === product._id && item.variant === variant
                );

                let newItems: CartItem[];
                if (existingItemIndex >= 0) {
                    // Product already in cart - increase quantity
                    newItems = [...items];
                    newItems[existingItemIndex] = {
                        ...newItems[existingItemIndex],
                        quantity: newItems[existingItemIndex].quantity + quantity,
                    };
                } else {
                    // New product - add to cart
                    const newItem: CartItem = {
                        ...product,
                        quantity,
                        variant,
                    };
                    newItems = [...items, newItem];
                }

                // Update local state immediately
                updateState(newItems);

                // Sync with server
                try {
                    await api.post('/cart/items', { productId: product._id, quantity, variant });
                    // Fetch cart again to get cartItemId for newly added items
                    await get().fetchCart();
                    toast.success('Đã thêm vào giỏ hàng!');
                } catch (apiError) {
                    // Revert on error
                    updateState(items);
                    throw apiError;
                }
            } catch (error) {
                toast.error('Không thể thêm vào giỏ hàng');
                throw error;
            } finally {
                set({ isLoading: false });
            }
        },

        removeFromCart: async (productId: string) => {
            const { items } = get();
            const item = items.find((i) => i._id === productId);
            if (!item || !item.cartItemId) {
                toast.error('Không tìm thấy sản phẩm trong giỏ hàng');
                return;
            }

            const newItems = items.filter((i) => i._id !== productId);

            // Optimistic update
            updateState(newItems);

            try {
                await api.delete(`/cart/items/${item.cartItemId}`);
                toast.success('Đã xóa khỏi giỏ hàng');
            } catch (error) {
                // Revert on error
                updateState(items);
                toast.error('Không thể xóa sản phẩm');
                throw error;
            }
        },

        increaseQuantity: async (productId: string) => {
            const { items } = get();
            const item = items.find((i) => i._id === productId);
            if (!item || !item.cartItemId) {
                toast.error('Không tìm thấy sản phẩm trong giỏ hàng');
                return;
            }

            const newQuantity = Math.min(item.quantity + 1, item.stock);
            const newItems = items.map((i) => {
                if (i._id === productId) {
                    return { ...i, quantity: newQuantity };
                }
                return i;
            });

            // Optimistic update
            updateState(newItems);

            try {
                await api.put(`/cart/items/${item.cartItemId}`, { quantity: newQuantity });
            } catch (error) {
                // Revert on error
                updateState(items);
                toast.error('Không thể cập nhật số lượng');
                throw error;
            }
        },

        decreaseQuantity: async (productId: string) => {
            const { items } = get();
            const item = items.find((i) => i._id === productId);
            if (!item || !item.cartItemId) {
                toast.error('Không tìm thấy sản phẩm trong giỏ hàng');
                return;
            }

            const newQuantity = Math.max(item.quantity - 1, 1);
            const newItems = items
                .map((i) => {
                    if (i._id === productId) {
                        return { ...i, quantity: newQuantity };
                    }
                    return i;
                })
                .filter((i) => i.quantity > 0); // Remove if quantity becomes 0

            // Optimistic update
            updateState(newItems);

            try {
                if (newQuantity > 0) {
                    await api.put(`/cart/items/${item.cartItemId}`, { quantity: newQuantity });
                } else {
                    await api.delete(`/cart/items/${item.cartItemId}`);
                }
            } catch (error) {
                // Revert on error
                updateState(items);
                toast.error('Không thể cập nhật số lượng');
                throw error;
            }
        },

        updateQuantity: async (productId: string, quantity: number) => {
            if (quantity <= 0) {
                get().removeFromCart(productId);
                return;
            }

            const { items } = get();
            const item = items.find((i) => i._id === productId);
            if (!item || !item.cartItemId) {
                toast.error('Không tìm thấy sản phẩm trong giỏ hàng');
                return;
            }

            const newQuantity = Math.min(quantity, item.stock);
            const newItems = items.map((i) => {
                if (i._id === productId) {
                    return { ...i, quantity: newQuantity };
                }
                return i;
            });

            // Optimistic update
            updateState(newItems);

            try {
                await api.put(`/cart/items/${item.cartItemId}`, { quantity: newQuantity });
            } catch (error) {
                // Revert on error
                updateState(items);
                toast.error('Không thể cập nhật số lượng');
                throw error;
            }
        },

        clearCart: async () => {
            const { items } = get();

            // Optimistic update
            set({ items: [], totalItems: 0, totalPrice: 0 });

            try {
                await api.delete('/cart');
                toast.success('Đã xóa giỏ hàng');
            } catch (error) {
                // Revert on error
                updateState(items);
                console.error('Failed to clear cart:', error);
            }
        },
    };
});

export default useCartStore;
